import React, { useLayoutEffect, useRef, useState } from 'react';
import { useInvoices, calcTotals } from '../../context/InvoicesContext';
import { fmtMoney, fmtDate, fmtDateFull } from '../../lib/format';

const TEMPLATE_SRC = '/invoice-templates/lve-invoice-blank-1.jpg';

// Default (design-intent) top offsets, as % of the page height — used when content is short
// enough to fit the branded template as originally designed.
const DEFAULT_TOP = { totals: 61, bank: 76, footer: 78, notes: 92 };
const GAP_PCT = 2; // breathing room (in % of page height) between stacked blocks

/**
 * InvoicePreview — LVE Social branded invoice.
 * Uses the user-provided blank template (pink swirls + LVE SOCIAL wordmark) as the background,
 * and renders ALL invoice text dynamically on top so it's fully editable per invoice.
 *
 * Blocks below the line-items table are stacked using measured heights rather than fixed %
 * offsets, so a longer line-item list (or wrapped descriptions) pushes totals/bank/notes/footer
 * down instead of overlapping them. Short invoices render at the original design positions.
 */
const InvoicePreview = ({ invoice, sender = {} }) => {
  const { brandMap, talentMap } = useInvoices();
  const brand = brandMap[invoice.brand_id || invoice.brandId];
  const talent = talentMap[invoice.talent_id || invoice.talentId];
  const totals = calcTotals(invoice);
  const items = invoice.line_items || invoice.lineItems || [];
  const taxRate = invoice.tax_rate ?? invoice.taxRate ?? 0;
  const altAccount = invoice.bank_account_id ? (sender?.additional_accounts || []).find((a) => a.id === invoice.bank_account_id) : null;
  const bank = altAccount || sender;
  const hasBank = Boolean(bank?.bankName || bank?.accountNumber || bank?.iban);

  const boldLabel = { fontWeight: 700, fontSize: '13px', letterSpacing: '0.06em', color: '#333' };
  const bodyText = { fontSize: '13px', color: '#333', lineHeight: 1.6 };

  const containerRef = useRef(null);
  const itemsRef = useRef(null);
  const totalsRef = useRef(null);
  const bankRef = useRef(null);
  const [top, setTop] = useState(DEFAULT_TOP);

  useLayoutEffect(() => {
    const containerH = containerRef.current?.getBoundingClientRect().height;
    if (!containerH) return;
    const pct = (px) => (px / containerH) * 100;

    const itemsBottomPct = 42 + pct(itemsRef.current?.getBoundingClientRect().height || 0);
    const totalsTop = Math.max(DEFAULT_TOP.totals, itemsBottomPct + GAP_PCT);
    const totalsBottomPct = totalsTop + pct(totalsRef.current?.getBoundingClientRect().height || 0);

    const bankTop = Math.max(DEFAULT_TOP.bank, totalsBottomPct + GAP_PCT);
    const footerTop = bankTop; // footer sits alongside bank details in the original design
    const bankBottomPct = hasBank ? bankTop + pct(bankRef.current?.getBoundingClientRect().height || 0) : totalsBottomPct;
    const notesTop = Math.max(DEFAULT_TOP.notes, bankBottomPct + GAP_PCT);

    setTop({ totals: totalsTop, bank: bankTop, footer: footerTop, notes: notesTop });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, JSON.stringify(items), taxRate, invoice.notes, invoice.po_number, hasBank, bank?.bankName, bank?.accountNumber, bank?.iban]);

  return (
    <div className="mx-auto max-w-3xl print:max-w-none print:m-0" id="invoice-print-area">
      <div
        ref={containerRef}
        className="relative w-full"
        style={{
          aspectRatio: '210 / 297',
          backgroundImage: `url("${TEMPLATE_SRC}")`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#ffffff',
          color: '#333',
          fontFamily: 'ui-sans-serif, system-ui, "Segoe UI", "Helvetica Neue", sans-serif',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }}
      >
        {/* Issued to */}
        <div className="absolute" style={{ left: '9%', top: '27%', width: '42%' }}>
          <div style={boldLabel}>ISSUED TO:</div>
          <div style={{ ...bodyText, marginTop: '8px', whiteSpace: 'pre-line' }}>
            {invoice.bill_to || brand?.name || '—'}
          </div>
          {talent && !invoice.bill_to && <div style={{ fontSize: '12px', color: '#777', marginTop: '2px' }}>re: {talent.name}</div>}
        </div>

        {/* Invoice no / Date / Due date / PO number */}
        <div className="absolute" style={{ right: '9%', top: '27%', width: '38%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: '12px', rowGap: '8px' }}>
            <div style={boldLabel}>INVOICE NO:</div>
            <div style={{ ...bodyText, textAlign: 'right', fontWeight: 600 }}>{invoice.number || '—'}</div>
            <div style={boldLabel}>DATE:</div>
            <div style={{ ...bodyText, textAlign: 'right' }}>{fmtDateFull(invoice.issue_date || invoice.issueDate)}</div>
            <div style={boldLabel}>DUE DATE:</div>
            <div style={{ ...bodyText, textAlign: 'right' }}>{fmtDateFull(invoice.due_date || invoice.dueDate)}</div>
            {invoice.po_number && (
              <>
                <div style={boldLabel}>PO NUMBER:</div>
                <div style={{ ...bodyText, textAlign: 'right' }}>{invoice.po_number}</div>
              </>
            )}
            {invoice.status === 'paid' && (invoice.paid_date || invoice.paidDate) && (
              <>
                <div style={{ ...boldLabel, color: '#059669' }}>PAID:</div>
                <div style={{ ...bodyText, textAlign: 'right', color: '#059669', fontWeight: 600 }}>{fmtDate(invoice.paid_date || invoice.paidDate)}</div>
              </>
            )}
          </div>
        </div>

        {/* Line items table */}
        <div ref={itemsRef} className="absolute" style={{ left: '9%', right: '9%', top: '42%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 0.7fr 1fr', gap: '10px', padding: '12px 14px', background: '#e8dccb', ...boldLabel, fontSize: '12px' }}>
            <div>DESCRIPTION</div>
            <div style={{ textAlign: 'right' }}>UNIT PRICE</div>
            <div style={{ textAlign: 'right' }}>QTY</div>
            <div style={{ textAlign: 'right' }}>TOTAL</div>
          </div>
          <div>
            {items.map((li, idx) => {
              const unit = Number(li.unit_price ?? li.unitPrice) || 0;
              const qty = Number(li.quantity) || 0;
              const amount = qty * unit;
              return (
                <div key={li.id || idx} style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 0.7fr 1fr', gap: '10px', padding: '12px 14px', ...bodyText, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <div style={{ wordBreak: 'break-word' }}>{li.description || '—'}</div>
                  <div style={{ textAlign: 'right' }}>{fmtMoney(unit, invoice.currency)}</div>
                  <div style={{ textAlign: 'right' }}>{qty}</div>
                  <div style={{ textAlign: 'right', fontWeight: 500 }}>{fmtMoney(amount, invoice.currency)}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Totals — positioned below line items (pushed down automatically if items overflow) */}
        <div ref={totalsRef} className="absolute" style={{ left: '9%', right: '9%', top: `${top.totals}%` }}>
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 0.7fr 1fr', gap: '10px', padding: '8px 14px', ...bodyText }}>
            <div style={{ gridColumn: '1 / span 3', ...boldLabel, textAlign: 'right' }}>SUBTOTAL</div>
            <div style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(totals.subtotal, invoice.currency)}</div>
          </div>
          {taxRate > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 0.7fr 1fr', gap: '10px', padding: '8px 14px', ...bodyText }}>
              <div style={{ gridColumn: '1 / span 3', ...boldLabel, textAlign: 'right' }}>VAT ({taxRate}%)</div>
              <div style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(totals.tax, invoice.currency)}</div>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 0.7fr 1fr', gap: '10px', padding: '14px 14px', marginTop: '8px', background: '#e8dccb' }}>
            <div style={{ gridColumn: '1 / span 3', textAlign: 'right', ...boldLabel, fontSize: '14px' }}>TOTAL</div>
            <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '15px' }}>{fmtMoney(totals.total, invoice.currency)}</div>
          </div>
        </div>

        {/* Bank details */}
        {hasBank && (
          <div ref={bankRef} className="absolute" style={{ left: '9%', top: `${top.bank}%`, width: '55%' }}>
            <div style={{ ...boldLabel, marginBottom: '8px', fontSize: '13px' }}>BANK DETAILS</div>
            <div style={bodyText}>
              {bank?.bankName && <div>Bank Name: {bank.bankName}</div>}
              {bank?.accountName && <div>Account Name: {bank.accountName}</div>}
              {bank?.bankAddress && <div>Bank address: {bank.bankAddress}</div>}
              {bank?.accountNumber && <div>Account number: {bank.accountNumber}</div>}
              {bank?.sortCode && <div>Sort code: {bank.sortCode}</div>}
              {bank?.swift && <div>SWIFT code: {bank.swift}</div>}
              {bank?.iban && <div>IBAN: {bank.iban}</div>}
            </div>
          </div>
        )}

        {invoice.notes && (
          <div className="absolute" style={{ left: '9%', right: '40%', top: `${top.notes}%`, ...bodyText, color: '#666', whiteSpace: 'pre-line', fontSize: '11px' }}>
            {invoice.notes}
          </div>
        )}

        {/* Footer — sender business details, upper right area to sit in white gap before bottom blob */}
        <div className="absolute text-right" style={{ right: '9%', top: `${top.footer}%`, width: '38%', color: '#a08cb5', fontSize: '12px', lineHeight: 1.6 }}>
          {sender?.name && <div style={{ fontWeight: 700, color: '#a08cb5', letterSpacing: '0.05em', fontSize: '13px' }}>{sender.name}</div>}
          {sender?.address && <div style={{ whiteSpace: 'pre-line' }}>{sender.address.replace(/\n/g, ', ')}</div>}
          {sender?.companyRegistration && <div>Company Registration: {sender.companyRegistration}</div>}
          {sender?.vatNumber && <div>VAT number: {sender.vatNumber}</div>}
          {sender?.email && <div>{sender.email}</div>}
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
